import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Avatar,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

import {
  FaSearch,
  FaCheck,
  FaTimes,
  FaCalendarAlt,
  FaUserGraduate,
  FaFileExcel,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import api from "../../api";
import "./StudentAttendanceList.css";

/* =========================================================
   HELPERS
   ========================================================= */

const getStoredAcademicYearId = () => {
  const keys = [
    "academic_year_id",
    "academicYearId",
    "selected_academic_year_id",
    "selectedAcademicYearId",
    "current_academic_year_id",
    "currentAcademicYearId",
  ];

  for (const key of keys) {
    const value = localStorage.getItem(key);
    if (value && String(value).trim() !== "") {
      return String(value).trim();
    }
  }

  const objectKeys = [
    "academic_year",
    "academicYear",
    "selected_academic_year",
    "current_academic_year",
  ];

  for (const key of objectKeys) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const id =
        parsed?.id ?? parsed?.academic_year_id ?? parsed?.academicYearId;
      if (id) return String(id);
    } catch {
      // ignore
    }
  }

  return "";
};

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.data?.students)) return payload.data.students;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const isApiOk = (payload) =>
  payload?.status === true ||
  payload?.success === true ||
  payload?.status === 1;

/* =========================================================
   STUDENT ATTENDANCE PAGE
   ========================================================= */

const StudentAttendanceList = () => {
  const navigate = useNavigate();

  /* =========================================================
     AUTH
     ========================================================= */
  const schoolId = localStorage.getItem("school_id");
  const token = localStorage.getItem("token");

  /* =========================================================
     TODAY
     ========================================================= */
  const getToday = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /* =========================================================
     STATES
     ========================================================= */
  const [attendanceDate, setAttendanceDate] = useState(getToday());
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [studentsLoaded, setStudentsLoaded] = useState(false);
  const [academicYearId, setAcademicYearId] = useState(
    getStoredAcademicYearId()
  );
  const [academicYearLabel, setAcademicYearLabel] = useState("");

  /* =========================================================
     AUTH CHECK
     ========================================================= */
  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  /* =========================================================
     AUTH HEADERS
     ========================================================= */
  const getHeaders = () => {
    const currentToken = localStorage.getItem("token");
    const currentSchoolId = localStorage.getItem("school_id");

    return {
      Authorization: currentToken ? `Bearer ${currentToken}` : "",
      "X-School-Id": currentSchoolId || "",
      Accept: "application/json",
      "Content-Type": "application/json",
    };
  };

  /* =========================================================
     AUTH HELPER
     ========================================================= */
  const ensureAuth = () => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
      navigate("/login");
      return false;
    }
    return true;
  };

  /* =========================================================
     FETCH CLASSES
     ========================================================= */
  const fetchClasses = async () => {
    if (!ensureAuth()) return;
    setLoadingClasses(true);
    try {
      let list = [];

      try {
        const res = await api.get("/get-classes", {
          headers: getHeaders(),
        });
        list = extractList(res.data);
      } catch (firstError) {
        console.warn("get-classes failed, trying student-attendance/classes", firstError);
      }

      if (!list.length) {
        const res = await api.get("/student-attendance/classes", {
          headers: getHeaders(),
          params: {
            academic_year_id: academicYearId || getStoredAcademicYearId() || undefined,
          },
        });
        list = extractList(res.data);
      }

      setClasses(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Class Fetch Error:", error);
      setClasses([]);
      Swal.fire("Error", "Failed to fetch classes.", "error");
    } finally {
      setLoadingClasses(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
     ========================================================= */
  useEffect(() => {
    if (!ensureAuth()) return;

    const loadAcademicYear = async () => {
      let yearId = getStoredAcademicYearId();

      try {
        const res = await api.get("/school/academic_year/list", {
          headers: getHeaders(),
        });
        const years = extractList(res.data);
        const selected =
          years.find(
            (item) =>
              String(item.id ?? item.academic_year_id) === String(yearId)
          ) ||
          years.find(
            (item) => String(item.status || "").toLowerCase() === "active"
          ) ||
          years[0];

        if (selected) {
          yearId = String(selected.id ?? selected.academic_year_id ?? "");
          setAcademicYearLabel(
            selected.session_name ||
              selected.name ||
              selected.academic_year ||
              ""
          );
          if (yearId) {
            localStorage.setItem("academic_year_id", yearId);
          }
        }
      } catch (error) {
        console.warn("Academic year load skipped:", error);
      }

      setAcademicYearId(yearId);
      fetchClasses();
    };

    loadAcademicYear();
  }, []);

  /* =========================================================
     FETCH SECTIONS WHEN CLASS CHANGES
     ========================================================= */
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection(null);
      setStudents([]);
      setStudentsLoaded(false);
      return;
    }

    const fetchSections = async () => {
      setLoadingSections(true);
      try {
        const classId = selectedClass.id;
        const res = await api.get(`/get-sections/${classId}`, {
          headers: getHeaders(),
        });

        let list = extractList(res.data);

        if (!list.length) {
          const fallback = await api.get("/student-attendance/sections", {
            headers: getHeaders(),
            params: {
              class_id: classId,
              academic_year_id: academicYearId || getStoredAcademicYearId() || undefined,
            },
          });
          list = extractList(fallback.data);
        }

        setSections(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("Section Fetch Error:", error);
        setSections([]);
        setSelectedSection(null);
      } finally {
        setLoadingSections(false);
      }
    };

    fetchSections();
  }, [selectedClass]);

  /* =========================================================
     CLASS CHANGE
     ========================================================= */
  const handleClassChange = (_event, newValue) => {
    setSelectedClass(newValue);
    setSelectedSection(null);
    setStudents([]);
    setStudentsLoaded(false);
    setSearchQuery("");
  };

  /* =========================================================
     SECTION CHANGE
     ========================================================= */
  const handleSectionChange = (_event, newValue) => {
    setSelectedSection(newValue);
    setStudents([]);
    setStudentsLoaded(false);
    setSearchQuery("");
  };

  /* =========================================================
     FETCH STUDENTS + EXISTING ATTENDANCE
     ========================================================= */
  const fetchStudents = async () => {
    if (!ensureAuth()) return;

    if (!attendanceDate) {
      Swal.fire("Required", "Please select attendance date.", "warning");
      return;
    }

    if (!selectedClass) {
      Swal.fire("Required", "Please select class.", "warning");
      return;
    }

    if (!selectedSection) {
      Swal.fire("Required", "Please select section.", "warning");
      return;
    }

    setLoadingStudents(true);
    setStudentsLoaded(false);
    setSearchQuery("");

    try {
      const yearId =
        academicYearId || getStoredAcademicYearId();

      const params = {
        date: attendanceDate,
        class_id: Number(selectedClass.id),
        section_id: Number(selectedSection.id),
        academic_year_id: yearId || undefined,
        year_id: yearId || undefined,
      };

      console.log("ATTENDANCE FETCH PARAMS:", params);

      const res = await api.get("/student-attendance/students", {
        params,
        headers: getHeaders(),
      });

      console.log("ATTENDANCE FETCH RESPONSE:", res.data);

      const data = res.data;
      const studentList = extractList(data);

      if (isApiOk(data) || studentList.length > 0) {
        const normalizedStudents = studentList.map((student) => {
          const rawStatus = String(
            student.attendance_status || student.status || ""
          )
            .trim()
            .toLowerCase();

          let status = "not_marked";

          if (rawStatus === "present") {
            status = "present";
          } else if (rawStatus === "absent") {
            status = "absent";
          } else if (rawStatus === "leave") {
            status = "leave";
          } else if (rawStatus === "holiday") {
            status = "holiday";
          } else {
            status = "not_marked";
          }

          return {
            ...student,
            student_id: student.student_id || student.id,
            attendance_status: status,
          };
        });

        setStudents(normalizedStudents);
        setStudentsLoaded(true);

        if (normalizedStudents.length === 0) {
          Swal.fire({
            icon: "info",
            title: "No Students Found",
            text: "No students found for the selected class and section.",
            confirmButtonText: "OK",
            confirmButtonColor: "#2563eb",
          });
        }
      } else {
        setStudents([]);
        setStudentsLoaded(true);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Students",
          text:
            data?.message ||
            "No students found for the selected class and section.",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
        });
      }
    } catch (error) {
      console.error("Attendance Student Fetch Error:", error);
      console.error("Attendance Error Response:", error?.response?.data);

      setStudents([]);
      setStudentsLoaded(true);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Students",
        text:
          error?.response?.data?.message ||
          "Something went wrong while loading students.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoadingStudents(false);
    }
  };

  /* =========================================================
     CHANGE STUDENT STATUS
     ========================================================= */
  const changeStudentStatus = (studentId, status) => {
    setStudents((previousStudents) => {
      return previousStudents.map((student) => {
        const id = student.student_id || student.id;

        if (String(id) === String(studentId)) {
          return {
            ...student,
            attendance_status: status,
          };
        }

        return student;
      });
    });
  };

  /* =========================================================
     MARK ALL PRESENT
     ========================================================= */
  const markAllPresent = () => {
    setStudents((previousStudents) => {
      return previousStudents.map((student) => ({
        ...student,
        attendance_status: "present",
      }));
    });
  };

  /* =========================================================
     MARK ALL ABSENT
     ========================================================= */
  const markAllAbsent = () => {
    setStudents((previousStudents) => {
      return previousStudents.map((student) => ({
        ...student,
        attendance_status: "absent",
      }));
    });
  };

  /* =========================================================
     RESET
     ========================================================= */
  const resetAttendance = () => {
    setStudents((previousStudents) => {
      return previousStudents.map((student) => ({
        ...student,
        attendance_status: "present",
      }));
    });
  };

  /* =========================================================
     EXPORT TO EXCEL
     ========================================================= */
  const exportToExcel = () => {
    if (!students || students.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "Please load student list first to download Excel sheet.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const excelData = students.map((student, index) => {
      const id = student.student_id || student.id;
      const name = student.name || student.student_name || "-";
      const code = student.student_code || "-";
      const roll = student.roll_no || "-";
      const mobile = student.mobile || "-";
      const status = (student.attendance_status || "not_marked").toUpperCase();

      return {
        "SL No": index + 1,
        "Student ID": id,
        "Student Name": name,
        "Student Code": code,
        "Roll No": roll,
        "Mobile": mobile,
        "Class": selectedClass?.name || selectedClass?.class_name || "-",
        "Section": selectedSection?.name || selectedSection?.section_name || "-",
        "Date": attendanceDate,
        "Attendance Status": status,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

    const className =
      selectedClass?.name || selectedClass?.class_name || "Class";
    const sectionName =
      selectedSection?.name || selectedSection?.section_name || "Section";
    const fileName = `Attendance_${className}_${sectionName}_${attendanceDate}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  /* =========================================================
     SEARCH
     ========================================================= */
  const filteredStudents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return students;
    }

    return students.filter((student) => {
      const name = String(
        student.name || student.student_name || ""
      ).toLowerCase();

      const code = String(student.student_code || "").toLowerCase();
      const mobile = String(student.mobile || "").toLowerCase();
      const roll = String(student.roll_no || "").toLowerCase();

      return (
        name.includes(query) ||
        code.includes(query) ||
        mobile.includes(query) ||
        roll.includes(query)
      );
    });
  }, [students, searchQuery]);

  /* =========================================================
     SUMMARY
     ========================================================= */
  const attendanceSummary = useMemo(() => {
    const total = students.length;

    const present = students.filter(
      (student) => student.attendance_status === "present"
    ).length;

    const absent = students.filter(
      (student) => student.attendance_status === "absent"
    ).length;

    const leave = students.filter(
      (student) => student.attendance_status === "leave"
    ).length;

    return {
      total,
      present,
      absent,
      leave,
    };
  }, [students]);

  /* =========================================================
     SAVE ATTENDANCE
     ========================================================= */
  const saveAttendance = async () => {
    if (!students.length) {
      Swal.fire({
        icon: "info",
        title: "No Students",
        text: "There are no students to save.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!attendanceDate) {
      Swal.fire("Required", "Please select attendance date.", "warning");
      return;
    }

    if (!selectedClass) {
      Swal.fire("Required", "Please select class.", "warning");
      return;
    }

    if (!selectedSection) {
      Swal.fire("Required", "Please select section.", "warning");
      return;
    }

    setSavingAttendance(true);

    try {
      const yearId = academicYearId || getStoredAcademicYearId();

      const payload = {
        date: attendanceDate,
        class_id: Number(selectedClass.id),
        section_id: Number(selectedSection.id),
        academic_year_id: yearId ? Number(yearId) : undefined,
        students: students.map((student) => ({
          student_id: Number(student.student_id || student.id),
          status:
            student.attendance_status === "absent"
              ? "absent"
              : student.attendance_status === "leave"
              ? "leave"
              : "present",
        })),
      };

      console.log("ATTENDANCE SAVE PAYLOAD:", payload);

      const res = await api.post("/student-attendance/save", payload, {
        headers: getHeaders(),
      });

      console.log("ATTENDANCE SAVE RESPONSE:", res.data);

      if (res.data?.status === true || res.data?.success === true) {
        await fetchStudents();

        Swal.fire({
          icon: "success",
          title: "Attendance Saved",
          text: res.data?.message || "Student attendance saved successfully.",
          confirmButtonText: "OK",
          confirmButtonColor: "#16a34a",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Save Failed",
          text: res.data?.message || "Failed to save attendance.",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Attendance Save Error:", error);
      console.error("Attendance Save Response:", error?.response?.data);

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: error?.response?.data?.message || "Unable to save attendance.",
        confirmButtonText: "OK",
      });
    } finally {
      setSavingAttendance(false);
    }
  };

  /* =========================================================
     DATE FORMAT
     ========================================================= */
  const formatDisplayDate = (dateString) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(`${dateString}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* =========================================================
     STUDENT PHOTO
     ========================================================= */
  const getStudentPhoto = (student) => {
    return (
      student.file ||
      student.photo ||
      student.image ||
      student.photo_url ||
      "/default-user.png"
    );
  };

  /* =========================================================
     STUDENT ID
     ========================================================= */
  const getStudentId = (student) => {
    return student.student_id || student.id;
  };

  /* =========================================================
     RENDER
     ========================================================= */
  return (
    <Box className="student-attendance-page">
      {/* =====================================================
          PAGE HEADER
          ===================================================== */}
      <Box
        className="attendance-page-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <Box className="attendance-title-icon">
            <FaUserGraduate />
          </Box>
          <Box>
            <Typography className="attendance-page-title">
              Student Attendance
            </Typography>

            <Typography className="attendance-page-subtitle">
              Manage daily student attendance class and section wise.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {/* EXCEL DOWNLOAD BUTTON */}
          <Button
            variant="contained"
            color="success"
            startIcon={<FaFileExcel />}
            onClick={exportToExcel}
            disabled={!studentsLoaded || students.length === 0}
            sx={{
              backgroundColor: "#16a34a",
              "&:hover": { backgroundColor: "#15803d" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Export Excel
          </Button>

          {/* DATE DISPLAY */}
          <Box className="attendance-date-display">
            <FaCalendarAlt />
            <Box>
              <span>Attendance Date</span>
              <strong>{formatDisplayDate(attendanceDate)}</strong>
            </Box>
          </Box>
        </Box>
      </Box>

      <Paper className="attendance-session-card" elevation={0}>
        <Box className="attendance-session-icon">
          <FaCalendarAlt />
        </Box>
        <Box>
          <span>Academic Session</span>
          <strong>
            {academicYearLabel ||
              (academicYearId
                ? `Session ID ${academicYearId}`
                : "Not selected")}
          </strong>
        </Box>
        <Box className="attendance-session-date">
          {formatDisplayDate(attendanceDate)}
        </Box>
      </Paper>

      {/* =====================================================
          FILTER CARD
          ===================================================== */}
      <Paper className="attendance-filter-card" elevation={0}>
        <Box className="attendance-filter-heading">
          <Typography className="filter-title">
            Attendance Details
          </Typography>

          <Typography className="filter-description">
            Select date, class and section to load students.
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* DATE */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Attendance Date"
              type="date"
              value={attendanceDate}
              onChange={(e) => {
                setAttendanceDate(e.target.value);
                setStudents([]);
                setStudentsLoaded(false);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* CLASS */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              options={classes}
              value={selectedClass}
              loading={loadingClasses}
              onChange={handleClassChange}
              getOptionLabel={(option) =>
                option?.name || option?.class_name || option?.title || ""
              }
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Class" />
              )}
            />
          </Grid>

          {/* SECTION */}
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              options={sections}
              value={selectedSection}
              loading={loadingSections}
              disabled={!selectedClass}
              onChange={handleSectionChange}
              getOptionLabel={(option) =>
                option?.name || option?.section_name || option?.title || ""
              }
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Section" />
              )}
            />
          </Grid>

          {/* LOAD BUTTON */}
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              className="load-students-button"
              onClick={fetchStudents}
              disabled={
                loadingStudents ||
                !attendanceDate ||
                !selectedClass ||
                !selectedSection
              }
              startIcon={
                loadingStudents ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FaSearch />
                )
              }
            >
              {loadingStudents ? "Loading..." : "Load Students"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* =====================================================
          SUMMARY
          ===================================================== */}
      <Box className="attendance-summary-grid">
        {/* TOTAL */}
        <Paper
          className="attendance-summary-card total-card"
          elevation={0}
        >
          <Box className="summary-icon total-icon">
            <FaUserGraduate />
          </Box>
          <Box>
            <span>Total Students</span>
            <strong>{attendanceSummary.total}</strong>
          </Box>
        </Paper>

        {/* PRESENT */}
        <Paper
          className="attendance-summary-card present-card"
          elevation={0}
        >
          <Box className="summary-icon present-icon">
            <FaCheck />
          </Box>
          <Box>
            <span>Present</span>
            <strong>{attendanceSummary.present}</strong>
          </Box>
        </Paper>

        {/* ABSENT */}
        <Paper
          className="attendance-summary-card absent-card"
          elevation={0}
        >
          <Box className="summary-icon absent-icon">
            <FaTimes />
          </Box>
          <Box>
            <span>Absent</span>
            <strong>{attendanceSummary.absent}</strong>
          </Box>
        </Paper>

        {/* LEAVE */}
        <Paper
          className="attendance-summary-card leave-card"
          elevation={0}
        >
          <Box className="summary-icon leave-icon">L</Box>
          <Box>
            <span>Leave</span>
            <strong>{attendanceSummary.leave}</strong>
          </Box>
        </Paper>
      </Box>

      {/* =====================================================
          STUDENT LIST
          ===================================================== */}
      {studentsLoaded && (
        <Paper className="attendance-list-card" elevation={0}>
          {/* LIST HEADER */}
          <Box className="student-list-header">
            <Box>
              <Typography className="student-list-title">
                Student List
              </Typography>

              <Typography className="student-list-subtitle">
                {selectedClass?.name || selectedClass?.class_name || ""}
                {" • "}
                {selectedSection?.name || selectedSection?.section_name || ""}
                {" • "}
                {formatDisplayDate(attendanceDate)}
              </Typography>
            </Box>

            <Box className="student-count-chip">
              {students.length} {" Students"}
            </Box>
          </Box>

          {/* TOOLBAR */}
          <Box className="attendance-toolbar">
            {/* SEARCH */}
            <TextField
              className="attendance-search"
              size="small"
              placeholder="Search student name, code or roll..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch />
                  </InputAdornment>
                ),
              }}
            />

            {/* ACTIONS */}
            <Box className="attendance-toolbar-actions">
              <Button
                variant="outlined"
                className="toolbar-button present-toolbar-button"
                startIcon={<FaCheck />}
                onClick={markAllPresent}
                disabled={!students.length}
              >
                Select All Present
              </Button>

              <Button
                variant="outlined"
                className="toolbar-button absent-toolbar-button"
                startIcon={<FaTimes />}
                onClick={markAllAbsent}
                disabled={!students.length}
              >
                Mark All Absent
              </Button>

              <Button
                variant="text"
                className="reset-button"
                onClick={resetAttendance}
                disabled={!students.length}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {/* TABLE */}
          <Box className="attendance-table-wrapper">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>STUDENT</th>
                  <th>STUDENT CODE</th>
                  <th>ROLL</th>
                  <th>MOBILE</th>
                  <th>ATTENDANCE</th>
                </tr>
              </thead>

              <tbody>
                {/* LOADING */}
                {loadingStudents ? (
                  <tr>
                    <td colSpan="6" className="loading-student-cell">
                      <CircularProgress size={28} />
                      <span>Loading students...</span>
                    </td>
                  </tr>
                ) : filteredStudents.length > 0 ? (
                  filteredStudents.map((student, index) => {
                    const studentId = getStudentId(student);
                    const status =
                      student.attendance_status || "not_marked";

                    return (
                      <tr key={studentId || index}>
                        {/* NUMBER */}
                        <td>
                          <span className="row-number">{index + 1}</span>
                        </td>

                        {/* STUDENT */}
                        <td>
                          <Box className="student-cell">
                            <Avatar
                              src={getStudentPhoto(student)}
                              className="student-avatar"
                              imgProps={{
                                onError: (e) => {
                                  e.currentTarget.src =
                                    "/default-user.png";
                                },
                              }}
                            />

                            <Box className="student-info">
                              <strong>
                                {student.name ||
                                  student.student_name ||
                                  "-"}
                              </strong>
                              <small>ID: {studentId}</small>
                            </Box>
                          </Box>
                        </td>

                        {/* CODE */}
                        <td>
                          <span className="table-text">
                            {student.student_code || "-"}
                          </span>
                        </td>

                        {/* ROLL */}
                        <td>
                          <span className="table-text">
                            {student.roll_no || "-"}
                          </span>
                        </td>

                        {/* MOBILE */}
                        <td>
                          <span className="table-text">
                            {student.mobile || "-"}
                          </span>
                        </td>

                        {/* ATTENDANCE BUTTONS */}
                        <td>
                          <Box className="status-button-group">
                            {/* PRESENT */}
                            <button
                              type="button"
                              className={`attendance-status-button present ${
                                status === "present" ? "active" : ""
                              }`}
                              onClick={() =>
                                changeStudentStatus(studentId, "present")
                              }
                            >
                              <FaCheck />
                              <span>Present</span>
                            </button>

                            {/* ABSENT */}
                            <button
                              type="button"
                              className={`attendance-status-button absent ${
                                status === "absent" ? "active" : ""
                              }`}
                              onClick={() =>
                                changeStudentStatus(studentId, "absent")
                              }
                            >
                              <FaTimes />
                              <span>Absent</span>
                            </button>

                            {/* LEAVE */}
                            <button
                              type="button"
                              className={`attendance-status-button leave ${
                                status === "leave" ? "active" : ""
                              }`}
                              onClick={() =>
                                changeStudentStatus(studentId, "leave")
                              }
                            >
                              <span className="leave-letter">L</span>
                              <span>Leave</span>
                            </button>
                          </Box>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  /* SEARCH NO RESULT */
                  <tr>
                    <td colSpan="6" className="no-student-cell">
                      <Box className="no-student-content">
                        <Box className="no-student-icon">
                          <FaUserGraduate />
                        </Box>
                        <strong>No Students Found</strong>
                        <span>
                          {searchQuery.trim()
                            ? "No student matches your search."
                            : "No students found for this class and section."}
                        </span>
                      </Box>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>

          {/* FOOTER */}
          <Box className="attendance-footer">
            <Box className="attendance-footer-summary">
              <span>
                Total: <strong>{attendanceSummary.total}</strong>
              </span>
              <span className="footer-present">
                Present: <strong>{attendanceSummary.present}</strong>
              </span>
              <span className="footer-absent">
                Absent: <strong>{attendanceSummary.absent}</strong>
              </span>
              <span className="footer-leave">
                Leave: <strong>{attendanceSummary.leave}</strong>
              </span>
            </Box>

            <Button
              variant="contained"
              className="save-attendance-button"
              onClick={saveAttendance}
              disabled={savingAttendance || students.length === 0}
              startIcon={
                savingAttendance ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FaCheck />
                )
              }
            >
              {savingAttendance ? "Saving..." : "Save Attendance"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* BEFORE LOAD */}
      {!studentsLoaded && !loadingStudents && (
        <Paper className="attendance-empty-state" elevation={0}>
          <Box className="empty-state-icon">
            <FaUserGraduate />
          </Box>
          <Typography className="empty-state-title">
            Ready to Take Attendance
          </Typography>
          <Typography className="empty-state-text">
            Select the attendance date, class and section. Then click{" "}
            <strong>Load Students</strong> to load students and their saved
            attendance.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default StudentAttendanceList;